import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Droplets, RotateCcw, Info, Image as ImageIcon } from 'lucide-react';

type BlurType = 'gaussian' | 'pixelate' | 'motion';
type OutputFormat = 'png' | 'jpg';

interface BlurPreset {
  label: string;
  value: number;
}

const BLUR_PRESETS: BlurPreset[] = [
  { label: 'Subtle', value: 3 },
  { label: 'Medium', value: 8 },
  { label: 'Strong', value: 15 },
  { label: 'Ultra', value: 25 },
];

const BLUR_TYPES: { value: BlurType; label: string; description: string }[] = [
  { value: 'gaussian', label: 'Gaussian', description: 'Smooth CSS filter blur' },
  { value: 'pixelate', label: 'Pixelate', description: 'Downscale + upscale blocks' },
  { value: 'motion', label: 'Motion Blur', description: 'Directional blur effect' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const BlurImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [blurType, setBlurType] = useState<BlurType>('gaussian');
  const [blurAmount, setBlurAmount] = useState(8);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const getSliderConfig = useCallback(() => {
    switch (blurType) {
      case 'gaussian': return { min: 1, max: 30, unit: 'px', label: 'Blur Radius' };
      case 'pixelate': return { min: 2, max: 40, unit: 'px', label: 'Block Size' };
      case 'motion': return { min: 1, max: 30, unit: 'px', label: 'Blur Distance' };
    }
  }, [blurType]);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const applyBlur = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    try {
      if (blurType === 'gaussian') {
        ctx.filter = `blur(${blurAmount}px)`;
        ctx.drawImage(img, 0, 0);
        ctx.filter = 'none';
      } else if (blurType === 'pixelate') {
        const blockSize = blurAmount;
        const smallW = Math.max(1, Math.ceil(w / blockSize));
        const smallH = Math.max(1, Math.ceil(h / blockSize));
        ctx.imageSmoothingEnabled = false;
        const offscreen = document.createElement('canvas');
        offscreen.width = smallW;
        offscreen.height = smallH;
        const offCtx = offscreen.getContext('2d');
        if (!offCtx) return;
        offCtx.drawImage(img, 0, 0, smallW, smallH);
        ctx.drawImage(offscreen, 0, 0, smallW, smallH, 0, 0, w, h);
        ctx.imageSmoothingEnabled = true;
      } else if (blurType === 'motion') {
        const steps = Math.max(1, blurAmount);
        ctx.globalAlpha = 1 / (steps + 1);
        for (let i = -steps; i <= steps; i++) {
          const offset = (i / steps) * blurAmount;
          ctx.drawImage(img, offset, 0);
        }
        ctx.globalAlpha = 1;
      }
    } catch (err) {
      setError('Failed to apply blur effect.');
    }
  }, [blurType, blurAmount]);

  // Live preview
  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    applyBlur(previewCanvasRef.current, imgElement);
  }, [imgElement, applyBlur]);

  const downloadResult = useCallback(() => {
    if (!imgElement || !file) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    applyBlur(canvas, imgElement);

    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = file.name.replace(/\.[^.]+$/, '');
      const ext = outputFormat === 'jpg' ? 'jpg' : 'png';
      link.download = `${name}_blurred.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, mimeType, quality);
  }, [imgElement, file, applyBlur, outputFormat, jpgQuality]);

  const reset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setImageDimensions(null);
    setBlurAmount(8);
    setBlurType('gaussian');
    setOutputFormat('png');
    setJpgQuality(92);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const sliderConfig = getSliderConfig();

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-sky-100 rounded-full">
            <Droplets className="w-8 h-8 text-sky-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Blur Image</h2>
        <p className="text-gray-600">
          Apply gaussian, pixelate, or motion blur effects with live preview.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-sky-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          {/* File Info */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm">
            <Info className="w-4 h-4 text-sky-600 flex-shrink-0" />
            <span className="font-medium text-sky-900">{file.name}</span>
            <span className="text-sky-700">{formatFileSize(file.size)}</span>
            {imageDimensions && (
              <span className="text-sky-700">{imageDimensions.w} x {imageDimensions.h}px</span>
            )}
          </div>

          {/* Blur Type Selection */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Blur Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {BLUR_TYPES.map((bt) => (
                <button
                  key={bt.value}
                  onClick={() => {
                    setBlurType(bt.value);
                    setBlurAmount(8);
                  }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors text-center ${
                    blurType === bt.value
                      ? 'border-sky-500 bg-sky-50 text-sky-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-sky-300'
                  }`}
                >
                  <span className="font-medium text-sm">{bt.label}</span>
                  <span className="text-xs opacity-75">{bt.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Blur Presets */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Quick Presets</label>
            <div className="flex flex-wrap gap-2">
              {BLUR_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => setBlurAmount(Math.min(preset.value, sliderConfig.max))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    blurAmount === Math.min(preset.value, sliderConfig.max)
                      ? 'bg-sky-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-sky-300 hover:text-sky-600'
                  }`}
                >
                  {preset.label} ({preset.value}{blurType === 'pixelate' ? 'px' : 'px'})
                </button>
              ))}
            </div>
          </div>

          {/* Blur Amount Slider */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-gray-700">{sliderConfig.label}</label>
              <span className="text-2xl font-bold text-sky-600">{blurAmount}{sliderConfig.unit}</span>
            </div>
            <input
              type="range"
              min={sliderConfig.min}
              max={sliderConfig.max}
              value={blurAmount}
              onChange={(e) => setBlurAmount(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{sliderConfig.min}{sliderConfig.unit}</span>
              <span>{sliderConfig.max}{sliderConfig.unit}</span>
            </div>
          </div>

          {/* Output Format */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Output Format</label>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex gap-2">
                {(['png', 'jpg'] as OutputFormat[]).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setOutputFormat(fmt)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium uppercase transition-colors ${
                      outputFormat === fmt
                        ? 'bg-sky-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-sky-300'
                    }`}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[200px]">
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-medium text-gray-600">JPG Quality</label>
                    <span className="text-xs font-bold text-gray-900">{jpgQuality}%</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-600"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Before/After Previews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                <ImageIcon className="w-4 h-4" />
                Original
              </h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
                {originalPreview && (
                  <img src={originalPreview} alt="Original" className="max-w-full max-h-[400px] object-contain" />
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                <Droplets className="w-4 h-4" />
                Blurred Preview
              </h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
                <canvas ref={previewCanvasRef} className="max-w-full max-h-[400px] object-contain" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-sky-600 text-white font-medium rounded-xl hover:bg-sky-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Blurred Image
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              title="Reset"
            >
              <RotateCcw className="w-5 h-5" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlurImage;
